import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Avatar } from "@fidget/common/components/avatar/avatar";
import { L10n } from "@fidget/common/i18n";
import { UserProfile } from "@fidget/common/models/userProfile";
import { getFriendlyDateString, Mode } from "@fidget/common/utils/timeFormatter";
import { UserPresenter } from "@fidget/common/utils/userPresenter";
import styles from "./chatNote.css";
import { FuzzyTime } from "@fidget/common/components/chat/FuzzyTime";

export interface Props {
  user: UserProfile;
  timestamp: number;
  text: string;
  compactMode?: boolean;
}

const showdown = require("showdown");
const converter = new showdown.Converter({
  simplifiedAutoLink: true,
  simpleLineBreaks: true,
  tables: true,
});

export const ChatNote = ({ user, timestamp, text, compactMode = false }: Props) => {
  const [collapsed, setCollapsed] = useState<boolean>(compactMode);
  const [needsToCollapse, setNeedsToCollapse] = useState<boolean>(false);
  const [realText, setRealText] = useState<HTMLElement>();

  useEffect(() => {
    checkIfNeedsToCollapse();
  }, []);

  useEffect(() => {
    checkIfNeedsToCollapse();
  }, [text]);

  const checkIfNeedsToCollapse = () => {
    if (compactMode && realText) {
      setNeedsToCollapse(realText.clientHeight !== realText.scrollHeight);
    }
  };

  const fromBot = (user as any).isBot;

  return (
    <div className={classNames(styles["note-container"], !compactMode && styles.margin)}>
      <div className={styles.note}>
        <div className={styles.header}>
          <div className={styles.avatar}>
            <Avatar user={user} />
          </div>
          <div className={styles.author}>
            <L10n id={"conversation.notes.from"} />
            <span className={styles.name}>{UserPresenter.from(user).displayedName}</span>
          </div>
          <div className={styles.time}>
            <FuzzyTime timestamp={timestamp} />
            <span className={styles.date}>
              {getFriendlyDateString(timestamp, { useToday: true, mode: Mode.SHORT })}
            </span>
          </div>
        </div>
        <div className={classNames(styles.content, { [styles.collapsed]: collapsed })}>
          {fromBot
            ?
            <div
              className={classNames(styles.text, {
                [styles.truncated]: needsToCollapse && collapsed
              })}
              ref={(el) => setRealText(el || undefined)}
              dangerouslySetInnerHTML={{
                __html: converter.makeHtml(text)
              }}
            ></div>
            :
            <div
              className={classNames(styles.text, {
                [styles.truncated]: needsToCollapse && collapsed
              })}
              ref={(el) => setRealText(el || undefined)}
            >
              {text}
            </div>
          }
        </div>
        {compactMode && needsToCollapse && (
          <div className={styles["read-more"]} onClick={() => setCollapsed(!collapsed)}>
            {(collapsed && <L10n id={"conversation.notes.readMore"} />) || (
              <L10n id={"conversation.notes.readLess"} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
